
import { MapRoute } from "../../../_models/planning-assistant.interface";
import * as mapboxgl from "mapbox-gl";
import { LineLayerContainer } from "../classes/tour-animation";
import { PAUtil } from "../classes/util";
import { PATechnician } from "../classes/technician";
import { PATourPlannerControl } from "./pa-tourplanner-control";

export class PAMapboxControl {

  private static _instance: PAMapboxControl
  public static get Instance()
  {
    return this._instance || (this._instance = new this());
  }

  private constructor(
  ) {
  }

  async generateLineLayers(
    routes: MapRoute[],
    map: mapboxgl.Map,
    config?: {
      use_exact_waypoints: boolean;
      color?: string;
      listeners?: {
        on_mouse_out: (id: string) => void;
        on_mouse_over: (ev: mapboxgl.MapMouseEvent & {
          features?: mapboxgl.MapboxGeoJSONFeature[] | undefined
        } & mapboxgl.EventData, id: string) => void,
        on_click?: (id: string) => void
      };
      prefix: string
    }
  ): Promise<LineLayerContainer[]> {
    const promises: Promise<number[][]>[] = []
    for (const route of routes) {
      promises.push(this.getRouteCoordinates(route, config?.use_exact_waypoints))
    }

    const line_layers: LineLayerContainer[] = []
    for (const promise of promises) {
      const route = routes[promises.indexOf(promise)]
      const coords = await promise
      if (coords) {
        const line_layer = this.addRouteToMap(
          coords,
          this.routeToID(route, 'route_' + (config?.prefix || '')),
          config?.color || route.color,
          route.line_width,
          route.dashed,
          route.popup_text,
          map,
          config?.listeners
        )
        if (line_layer) line_layers.push(line_layer)
      }
    }

    this.moveCustomLabelLayersUp(map)

    return line_layers
  }

  public moveCustomLabelLayersUp(map: mapboxgl.Map): void {
    for (const layer of map.getStyle().layers) {
      if (layer.id.includes('clabel')) {
        map.moveLayer(layer.id)
      }
    }
  }

  public addRouteToMap(
    coordinates: number[][],
    id: string, color: string,
    line_width: number,
    dashed: boolean,
    popup_text: string,
    map: mapboxgl.Map,
    listeners?: {
      on_mouse_over?: (ev: mapboxgl.MapMouseEvent & {
        features?: mapboxgl.MapboxGeoJSONFeature[] | undefined
      } & mapboxgl.EventData, id: string) => void,
      on_mouse_out?: (id: string) => void,
      on_click?: (id: string) => void
    }
  ): LineLayerContainer {

    if (!map.getSource(id + '_source')) {
      map.addSource(id + '_source', {
        'type': 'geojson',
        'lineMetrics': true,
        'data': {
          'type': 'Feature',
          "properties": {},
          'geometry': {
            'type': 'LineString',
            'coordinates': coordinates
          }
        }
      });
    }

    let layer = map.getLayer(id + '_layer')

    if (!layer) {
      map.addLayer(
        {
          'id': id + '_layer',
          'type': 'line',
          'source': id + '_source',
          'layout': {
            'line-join': 'round',
            'line-cap': 'round',
          },
          'paint': dashed ? {
              'line-color': color,
              "line-width": line_width,
              "line-dasharray": [0.2, 2],
            } :
            {
              'line-color': color,
              "line-width": line_width,
              'line-opacity': .75
            }
        }
      )
    }

    layer = map.getLayer(id + '_layer')

    if (listeners?.on_mouse_over) {
      map.on('mouseover', id + '_layer',  (e) => {
        listeners.on_mouse_over(e, id)
      })
    }
    if (listeners?.on_mouse_out) {
      map.on('mouseout', id + '_layer', (_) => {
        listeners.on_mouse_out(id)
      })
    }
    if (listeners?.on_click) {
      map.on('click', id + '_layer', (_) => {
        listeners.on_click(id)
      })
    }

    if (layer.type == 'line') return {layer: layer, color: color, line_length: this.coordinatesLineLength(coordinates), line_width: line_width}
    else return null
  }

  coordinatesLineLength(coordinates: number[][]): number {
    return coordinates.reduce((sum, coordinate: number[]) => {
      const idx = coordinates.indexOf(coordinate)
      if (idx < coordinates.length - 1) {
        const next_coordinate = coordinates[idx + 1]
        const lon_dif = next_coordinate[0] - coordinate[0]
        const lat_dif = next_coordinate[1] - coordinate[1]
        return sum + Math.sqrt(lon_dif * lon_dif + lat_dif * lat_dif)
      } else {
        return sum
      }
    },  0)
  }

  async getRouteCoordinates(route: MapRoute, use_exact_waypoints?: boolean): Promise<number[][]> {
    let coordinates: number[][]
    const id = this.routeToID(route, 'route')

    if (!PAUtil.compareObjects(route.from.coordinates, route.to.coordinates)) {
      if (use_exact_waypoints) {
        if (typeof localStorage[id] != 'undefined'){
          coordinates = this.parseCoordinatesString(localStorage[id])
        } else {
          coordinates = await this.getRouteWithExactWaypoints(route)
          localStorage[id] = this.coordinateListToString(coordinates)
        }
      } else {
        coordinates = [[route.from.coordinates.longitude, route.from.coordinates.latitude],[route.to.coordinates.longitude, route.to.coordinates.latitude]]
      }
    } else {
      coordinates = null
    }

    return coordinates
  }

  routeToID(route: MapRoute, prefix?: string): string {
    return `${prefix ? prefix : ''}${route.from.coordinates.latitude},${route.from.coordinates.longitude};${route.to.coordinates.latitude},${route.to.coordinates.longitude}:${route.color}`
  }

  public parseCoordinatesString(coordinates: string): number[][] {
    const res = []
    const coordinate_pairs = coordinates.split(';')
    for (const coordinate of coordinate_pairs) {
      res.push(coordinate.split(',').map(coordinate => Number.parseFloat(coordinate)))
    }
    return res
  }

  public coordinateListToString(coordinates: number[][]): string {
    let res = ''
    for (const coordinate of coordinates) {
      res = res + coordinate[0].toString() + ',' + coordinate[1].toString()
      if (coordinates.indexOf(coordinate) != coordinates.length - 1){
        res = res + ';'
      }
    }
    return res
  }

  async getRouteWithExactWaypoints(route: MapRoute): Promise<number[][]> {
    const mb_route = await route.from.getDistanceToLocation(route.to)
    return mb_route.geometry.coordinates
  }

  public deleteUUIDLayers(uuid: string, map: mapboxgl.Map){
    if (uuid) {
      try {
        map?.getStyle().layers?.forEach(layer => {
          if (layer.id.includes(uuid) && layer.type == 'line' && typeof layer.source == 'string') {
            map.removeLayer(layer.id)
            map.removeSource(layer.source)
          }
        })
      } catch (err) {
        console.log(err)
      }
    }
  }

  public getMapLngLatBounds(config?: {ignore_operations?: boolean, ignore_technicians?: boolean, technicians?: PATechnician[]}): mapboxgl.LngLatBounds {
    const bounds = new mapboxgl.LngLatBounds();

    (!config?.ignore_operations) ? PATourPlannerControl.Instance.shownMapOperations.map(operation => bounds.extend([operation.ticket.coordinates.longitude, operation.ticket.coordinates.latitude])) : {};
    (!config?.ignore_technicians) ? (config?.technicians || PATourPlannerControl.Instance.shownMapTechnicians).map(technician => bounds.extend([technician.coordinates.longitude, technician.coordinates.latitude])) : {};

    return bounds
  }
}